import { createAction, createReducer } from '@reduxjs/toolkit';
import { fromZonedTime } from 'date-fns-tz';
import moment, { Moment } from 'moment';
import { useMemo, useReducer } from 'react';
import {
    getTransportAssignmentDeliveryDates,
    getUnloadingAddress,
    isCapacityOrder,
} from '../../TransportAssignmentUtils';
import {
    AddressAndContact,
    OrganizationIdentifierType,
    SelectableUnloadingAddress,
    TransportAssignment,
} from '../../transportAssignment.types';
import { DELETE_ID, UNLOADING_OPTIONS_TYPE_SEPARATOR } from './UnloadingAddressFormGroup';
import { TransportAssignmentConfirmationFormState, TransportAssignmentData } from './confirmationForm.types';
import { isFormDisabled, validateConfirmationForm } from './confirmationFormValidation';
import { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { MeansOfTransportType } from '../../../sharedComponents/common.types';
import {
    usePostTransportCapacityOrderConfirmationMutation,
    usePostTransportOrderConfirmationMutation,
} from '../../../api/transportOrderApi';
import { ConfirmationFormParams } from '../TransportAssignmentConfirmationForm';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { getCapacityConfirmationPayload, getTransportOrderConfirmationPayload } from '../getConfirmationPayload';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { transportAssignmentsSlice } from '../../../reducers/transportAssignmentsSlice';
import { useDispatch } from 'react-redux';

const initialFormState: TransportAssignmentConfirmationFormState = {
    hasContentChanged: false,
    meansOfTransport: undefined,
    confirmedPickUpDate: undefined,
    confirmedDeliveryDate: undefined,
    unloadingAddress: { identifier: { id: '', type: OrganizationIdentifierType.BUYER } },
};

const setMeansOfTransportAction = createAction<{ id?: string; assetId?: string } | undefined>(
    'sidebar/setMeansOfTransport',
);
const setTrailerIdAction = createAction<string | undefined>('sidebar/setTrailerId');
const setConfirmedPickUpDateAction = createAction<Date | string>('sidebar/setConfirmedPickUpDate');
const setConfirmedDeliveryDateAction = createAction<Date | string>('sidebar/setConfirmedDelivery');
const setUnloadingAddressAction = createAction<AddressAndContact | undefined>('sidebar/setUnloadingAddress');

const removeLeadingAndDuplicateWhitespace = (input: string) => input.replace(/^\s+/, '')?.replace(/\s{2,}/, ' ');
const mapEmptyStringToUndefined = (input: string | undefined) => (input ? input : undefined);

const transportAssignmentConfirmationReducer = createReducer(initialFormState, (builder) => {
    builder.addCase(setMeansOfTransportAction, (state, action) => {
        const value = action.payload;

        const trimmedId =
            value?.id !== undefined
                ? mapEmptyStringToUndefined(removeLeadingAndDuplicateWhitespace(value?.id))
                : undefined;

        state.hasContentChanged = true;
        state.meansOfTransport = {
            ...state.meansOfTransport,
            id: trimmedId,
            assetId: value?.assetId,
            type: MeansOfTransportType.TRUCK,
        };
    });
    builder.addCase(setTrailerIdAction, (state, action) => {
        const value = action.payload;

        const trimmedId =
            value !== undefined ? mapEmptyStringToUndefined(removeLeadingAndDuplicateWhitespace(value)) : undefined;

        state.hasContentChanged = true;
        state.meansOfTransport = {
            ...state.meansOfTransport,
            trailerId: trimmedId,
            type: MeansOfTransportType.TRUCK,
        };
    });
    builder.addCase(setConfirmedPickUpDateAction, (state, action) => {
        state.hasContentChanged = true;
        state.confirmedPickUpDate = action.payload;
    });
    builder.addCase(setConfirmedDeliveryDateAction, (state, action) => {
        state.hasContentChanged = true;
        state.confirmedDeliveryDate = action.payload;
    });
    builder.addCase(setUnloadingAddressAction, (state, action) => {
        state.unloadingAddress = action.payload;
        state.hasContentChanged = true;
    });
});

const setUnloadingAddress = (
    value: SelectOption | undefined,
    selectableUnloadingAddresses: SelectableUnloadingAddress[] | undefined,
) => {
    if (value === undefined || value.id === DELETE_ID) {
        return setUnloadingAddressAction(undefined);
    }
    const unloadingAddressId = value.id.substring(
        value.id.indexOf(UNLOADING_OPTIONS_TYPE_SEPARATOR) + UNLOADING_OPTIONS_TYPE_SEPARATOR.length,
    );
    const unloadingAddressAndContact = selectableUnloadingAddresses?.find(
        (address) => address.identifier.id === unloadingAddressId,
    );
    return setUnloadingAddressAction(unloadingAddressAndContact);
};

const setConfirmedPickUpDate = (value: Moment | string, timeZone: string) => {
    return setConfirmedPickUpDateAction(moment.isMoment(value) ? fromZonedTime(value.toDate(), timeZone) : value);
};

const setConfirmedDeliveryDate = (value: Moment | string, timeZone: string) => {
    return setConfirmedDeliveryDateAction(moment.isMoment(value) ? fromZonedTime(value.toDate(), timeZone) : value);
};

const getTransportAssignmentData = (transportAssignment: TransportAssignment): TransportAssignmentData => {
    const { requestedDeliveryDate, timeZone: deliveryTimeZone } =
        getTransportAssignmentDeliveryDates(transportAssignment);

    return {
        orderIssuedAt: transportAssignment.orderIssuedAt,
        requestedPickUpDate: transportAssignment.loading.requestedPickUpDate,
        pickUpTimeZone: transportAssignment.loading.timeZone,
        requestedDeliveryDate,
        deliveryTimeZone,
    };
};

const getInitialFormState = (transportAssignment: TransportAssignment): TransportAssignmentConfirmationFormState => {
    const { confirmedDeliveryDate } = getTransportAssignmentDeliveryDates(transportAssignment);
    return {
        hasContentChanged: false,
        meansOfTransport: transportAssignment.meansOfTransport,
        confirmedPickUpDate: transportAssignment.loading.confirmedPickUpDate,
        confirmedDeliveryDate,
        unloadingAddress: getUnloadingAddress(transportAssignment),
    };
};

export const useTransportAssignmentConfirmationForm = (transportAssignment: TransportAssignment) => {
    const assignmentData = useMemo(() => getTransportAssignmentData(transportAssignment), [transportAssignment]);
    const reduxDispatch = useDispatch();
    const [state, dispatch] = useReducer(
        transportAssignmentConfirmationReducer,
        transportAssignment,
        getInitialFormState,
    );

    const [confirmTransportOrder, confirmationRequest] = usePostTransportOrderConfirmationMutation();
    const [confirmCapacityOrder, capacityConfirmationRequest] = usePostTransportCapacityOrderConfirmationMutation();

    const closeSidebar = () => {
        reduxDispatch(transportAssignmentsSlice.actions.deselectTransportAssignment());
    };

    const validation = useMemo(
        () => validateConfirmationForm(transportAssignment, assignmentData, state),
        [transportAssignment, assignmentData, state],
    );

    const onConfirmOrder = runInBackgroundCallback(async (formParams: ConfirmationFormParams): Promise<void> => {
        try {
            if (isCapacityOrder(transportAssignment)) {
                await confirmCapacityOrder(getCapacityConfirmationPayload(transportAssignment, formParams)).unwrap();
            } else {
                await confirmTransportOrder(
                    getTransportOrderConfirmationPayload(transportAssignment, formParams),
                ).unwrap();
            }
            showSuccessNotification('outboundPortal.notification.confirmTransportAssignment.success');
            closeSidebar();
        } catch (error: unknown) {
            handleQueryError(error);
        }
    });

    return {
        assignmentData,
        formState: state,
        validation,
        setMeansOfTransport: (value?: { id?: string; assetId?: string }) => dispatch(setMeansOfTransportAction(value)),
        setTrailerId: (value: string | undefined) => dispatch(setTrailerIdAction(value)),
        setUnloadingAddress: (
            value: SelectOption | undefined,
            selectableUnloadingAddresses: SelectableUnloadingAddress[] | undefined,
        ) => dispatch(setUnloadingAddress(value, selectableUnloadingAddresses)),
        setConfirmedPickUpDate: (value: Moment | string) =>
            dispatch(setConfirmedPickUpDate(value, assignmentData.pickUpTimeZone)),
        setConfirmedDeliveryDate: (value: Moment | string) => {
            dispatch(setConfirmedDeliveryDate(value, assignmentData.deliveryTimeZone));
        },
        onConfirmOrder,
        isLoading: confirmationRequest.isLoading || capacityConfirmationRequest.isLoading,
        isFormDisabled: isFormDisabled(transportAssignment),
    };
};
